import type * as maplibregl from "maplibre-gl";
import { TEXT_FONTS, textFont, textFontName, textFontStyle } from "../../core/textFonts";
import type { TextFont, TextFontStyle } from "../../core/textFonts";

const STYLES: TextFontStyle[] = ["regular", "bold", "italic"];

/** Les polices du catalogue sous les noms qu'emploie `text-font`. */
export const TEXT_FONT_FACES: maplibregl.FontFacesSpecification = Object.fromEntries(
  TEXT_FONTS.flatMap((font) =>
    STYLES.filter((style) => font.files[style]).map((style) => [
      textFontName(font, style),
      font.files[style]!.map((file) => ({ url: file.url, "unicode-range": file.unicodeRange })),
    ]),
  ),
);

/** `text-font` d'un texte : la police du catalogue nommée par la propriété `fontName`, sinon `fallback`. */
export function textFontExpression(fallback: maplibregl.ExpressionSpecification): maplibregl.ExpressionSpecification {
  const names = Object.keys(TEXT_FONT_FACES);
  return ["match", ["get", "fontName"], ...names.flatMap((name) => [name, ["literal", [name]]]), fallback] as unknown as maplibregl.ExpressionSpecification;
}

export function shapeTextFontName(fontId: string | undefined, style: TextFontStyle): string | null {
  const font = textFont(fontId);
  return font ? textFontName(font, style) : null;
}

type LoadState = "loading" | "loaded" | "failed";

const loadStates = new Map<string, LoadState>();
const loadListeners = new Set<() => void>();

function cssFamily(font: TextFont): string {
  return `tokamap-${font.id}`;
}

function startLoading(font: TextFont, style: TextFontStyle): void {
  const key = `${font.id}:${style}`;
  if (loadStates.has(key)) return;
  if (typeof FontFace === "undefined" || typeof document === "undefined" || !document.fonts) {
    loadStates.set(key, "failed");
    return;
  }
  loadStates.set(key, "loading");
  const faces = font.files[style]!.map(
    (file) =>
      new FontFace(cssFamily(font), `url(${file.url})`, {
        weight: style === "bold" ? "700" : "400",
        style: style === "italic" ? "italic" : "normal",
        unicodeRange: file.unicodeRange.join(","),
      }),
  );
  for (const face of faces) document.fonts.add(face);
  Promise.all(faces.map((face) => face.load())).then(
    () => {
      loadStates.set(key, "loaded");
      for (const listener of loadListeners) listener();
    },
    () => loadStates.set(key, "failed"),
  );
}

/**
 * Police CSS avec laquelle mesurer un texte, au format de `CanvasRenderingContext2D.font` sans la
 * taille. Le premier appel pour une police lance son chargement : d'ici là, la mesure se fait avec
 * la police par défaut, et `onTextFontLoaded` prévient quand mesurer de nouveau.
 */
export function measureFont(fontId: string | undefined, style: TextFontStyle): { variant: string; family: string } | null {
  const font = textFont(fontId);
  if (!font) return null;
  const drawn = textFontStyle(font, style);
  startLoading(font, drawn);
  const variant = drawn === "bold" ? "bold " : drawn === "italic" ? "italic " : "";
  return { variant, family: `"${cssFamily(font)}"` };
}

export function onTextFontLoaded(listener: () => void): () => void {
  loadListeners.add(listener);
  return () => loadListeners.delete(listener);
}

/** Famille CSS d'une police du catalogue, chargée dans la page : pour l'afficher hors de la carte, dans un sélecteur par exemple. */
export function textFontCssFamily(fontId: string): string | null {
  return measureFont(fontId, "regular")?.family ?? null;
}
